import { useDispatch, useSelector } from 'react-redux';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';

import { IState, Subscription } from '../../graphql/types';
import {
  updateExportVideoState,
  updateGiide as updateGiideStore,
  updateSubscription,
} from '../../actions';
import { exportVideoModal, ExportVideoState } from '../../types';
import { getSignedUrl } from '../../components/utils';
import useUser from '../useUser';
import { GetGiideQuery } from '../../graphql/api';
import { GraphQLResult } from '@aws-amplify/api';

const maxLengthForExportMS = 300000; // 5 minutes

const onExportVideoUpdate = gql`
  subscription onExportVideoUpdate($id: ID!) {
    onExportVideoUpdate(id: $id) {
      id
      status
      progress
      error
    }
  }
`;

const exportVideoStart = gql`
  mutation exportVideoStart($input: ExportVideoUpdateInput) {
    exportVideoStart(input: $input) {
      id
      status
      progress
      error
    }
  }
`;

const getGiideInfoQuery = gql`
  query getGiide($id: ID!) {
    getGiide(id: $id) {
      id
      steps {
        items {
          recording {
            length
            feedCards {
              items {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const getGiideInfo = async giideId => {
  return (await API.graphql(
    graphqlOperation(getGiideInfoQuery, { id: giideId })
  )) as GraphQLResult<GetGiideQuery>;
};

const getLengthOfGiide = giide => {
  if (giide.data && giide.data.getGiide?.steps) {
    return giide.data.getGiide.steps.items.reduce(
      // @ts-ignore
      (memo, item) => memo + item?.recording?.length || 0,
      0
    );
  } else {
    return 0;
  }
};

const getNumFeedCardsForGiide = giide => {
  if (giide.data && giide.data.getGiide?.steps) {
    return giide.data.getGiide.steps.items.reduce((memo, item) => {
      return memo + item?.recording?.feedCards?.items?.length || 0;
    }, 0);
  }
};

type OnExportVideoUpdate = {
  onExportVideoUpdate: ExportVideoState;
};

const useExportVideo = () => {
  const dispatch = useDispatch();

  const subscription = useSelector<IState, Subscription | undefined>(
    state => state.subscriptions.exportVideo
  );

  const exportState = useSelector<IState, ExportVideoState>(
    state => state.exportVideoState
  );

  const { user } = useUser();

  const exportVideoStartMutation = giideId => {
    const variables = {
      input: {
        id: giideId,
      },
    };

    try {
      API.graphql(graphqlOperation(exportVideoStart, variables));
    } catch (error) {
      console.log(error);
      dispatch(updateExportVideoState({ error: true }));
    }
  };

  const handleDownloadVideo = async id => {
    // Event handler for downloading a video that has ALREADY been exported
    const path = `public/${user?.tenantID}/${id}/video/${id}-video_export.mp4`;
    const signedUrl = await getSignedUrl(path);

    if (signedUrl.url) {
      try {
        const link = document.createElement('a');
        link.href = signedUrl.url;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleOpenExportModal = async (id, title) => {
    // User entrypoint for starting a video export
    let whichModalToShow;
    if (exportState.id) {
      whichModalToShow = exportVideoModal.alreadyProcessingError;
    } else {
      dispatch(
        updateExportVideoState({
          requestedGiideId: id,
          requestedGiideTitle: title,
          modal: exportVideoModal.loading,
        })
      );

      const giideDataQueryResult = await getGiideInfo(id);

      if (getLengthOfGiide(giideDataQueryResult) > maxLengthForExportMS) {
        whichModalToShow = exportVideoModal.maxLengthExceededError;
      } else if (getNumFeedCardsForGiide(giideDataQueryResult) > 50) {
        whichModalToShow = exportVideoModal.maxFeedCardsExceededError;
      } else {
        whichModalToShow = exportVideoModal.start;
      }
    }

    dispatch(
      updateExportVideoState({
        requestedGiideId: id,
        requestedGiideTitle: title,
        modal: whichModalToShow,
      })
    );
  };

  const startExportVideo = async () => {
    exportVideoStartMutation(exportState.requestedGiideId);
    subscribe(exportState.requestedGiideId);

    dispatch(
      updateExportVideoState({
        id: exportState.requestedGiideId,
        title: exportState.requestedGiideTitle,
        modal: exportVideoModal.none,
        progress: 0,
        error: undefined,
        status: undefined,
        downloadLink: undefined,
      })
    );
  };

  const subscribe = giideId => {
    if (subscription) {
      subscription.unsubscribe();
    }

    const observable = API.graphql(
      graphqlOperation(onExportVideoUpdate, {
        id: giideId,
      })
    );

    if ('subscribe' in observable) {
      const newSub = observable.subscribe({
        next: async (next: { value: { data: OnExportVideoUpdate } }) => {
          const result = next.value.data.onExportVideoUpdate;

          // stop subscribing if the backend is finished
          if (result.status === 'finished' || result.error) {
            newSub.unsubscribe();
            dispatch(updateSubscription('exportVideo', undefined));
          }

          // if finished, get the signed url for the user to download
          if (result.status === 'finished') {
            const path = `public/${user?.tenantID}/${result.id}/video/${result.id}-video_export.mp4`;

            const signedUrl = await getSignedUrl(path);

            if (signedUrl.url) {
              // TODO: Test for error
              result.downloadLink = signedUrl.url;
            }

            if (result.id) {
              const id: string = result.id;

              dispatch(
                updateGiideStore({
                  id,
                  exportVideoState: 'complete',
                })
              );
            }
          }

          // Update the application state
          dispatch(updateExportVideoState(result));
        },

        error: error => {
          const errorState = {
            id: giideId,
            error: true,
            status: 'Error subscribing',
          };
          dispatch(updateExportVideoState(errorState));
        },
      });

      dispatch(updateSubscription('exportVideo', newSub));
    }
  };

  return {
    exportState,
    startExportVideo,
    handleOpenExportModal,
    handleDownloadVideo,
  };
};

export default useExportVideo;
